import { useState, useCallback } from 'react';
import { FileText, Upload, Download, Trash2, Search, Eye, Lock } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { formatDateTime } from '@/utils/formatters';
import { useDebounce } from '@/hooks/useDebounce';
import toast from 'react-hot-toast';

interface Document {
  id: string;
  leadId: string;
  leadName: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  uploadedBy: string;
  createdAt: string;
  isEncrypted: boolean;
}

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'medical_report', label: 'Medical Report' },
  { value: 'insurance_card', label: 'Insurance Card' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'lab_report', label: 'Lab Report' },
  { value: 'discharge_summary', label: 'Discharge Summary' },
  { value: 'consent_form', label: 'Consent Form' },
  { value: 'other', label: 'Other' },
];

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  return '📁';
}

export function DocumentsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploading, setUploading] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  const { data, isLoading } = useQuery({
    queryKey: ['documents', page, debouncedSearch, categoryFilter],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        data: { data: Document[]; pagination: { total: number; totalPages: number; hasNext: boolean; hasPrev: boolean } };
      }>('/documents', {
        params: { page, limit: 20, search: debouncedSearch || undefined, category: categoryFilter || undefined },
      });
      return data.data;
    },
  });

  const deleteDoc = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Document deleted');
    },
    onError: () => toast.error('Failed to delete document'),
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'other');
        await apiClient.post('/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success(`${acceptedFiles.length} file(s) uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }, [queryClient]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.jpg', '.jpeg', '.png'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleDownload = async (doc: Document) => {
    try {
      const { data } = await apiClient.get<{ data: { url: string } }>(`/documents/${doc.id}/download`);
      window.open(data.data.url, '_blank');
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText size={24} className="text-brand-navy" />
            Documents
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {data?.pagination.total ?? 0} total documents — encrypted at rest (AES-256)
          </p>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
          isDragActive ? 'border-brand-cyan bg-cyan-50' : 'border-slate-200 hover:border-brand-cyan/50 bg-white'
        }`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Spinner />
            <p className="text-sm text-slate-500">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 bg-brand-navy/10 rounded-xl flex items-center justify-center">
              <Upload size={22} className="text-brand-navy" />
            </div>
            <p className="font-medium text-slate-700">
              {isDragActive ? 'Drop files here' : 'Drag & drop files or click to upload'}
            </p>
            <p className="text-xs text-slate-400">PDF, Images, Word documents up to 10MB</p>
            <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
              <Lock size={11} />
              All files are encrypted with AES-256 before storage
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 p-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-48">
            <Input
              placeholder="Search by file name or patient..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              leftIcon={<Search size={14} />}
            />
          </div>
          <div className="w-52">
            <Select
              options={CATEGORY_OPTIONS}
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              placeholder="All Categories"
            />
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['File', 'Category', 'Patient', 'Uploaded By', 'Size', 'Date', 'Actions'].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data?.data.map(doc => (
                    <tr key={doc.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getFileIcon(doc.mimeType)}</span>
                          <div>
                            <div className="font-medium text-slate-900 text-xs max-w-48 truncate">{doc.fileName}</div>
                            {doc.isEncrypted && (
                              <div className="flex items-center gap-1 text-xs text-amber-600">
                                <Lock size={10} /> Encrypted
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full capitalize">
                          {doc.category.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{doc.leadName}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{doc.uploadedBy}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono">{formatBytes(doc.fileSize)}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{formatDateTime(doc.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1.5 text-slate-400 hover:text-brand-cyan hover:bg-cyan-50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Delete "${doc.fileName}"?`)) {
                                deleteDoc.mutate(doc.id);
                              }
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!data?.data.length && (
                    <tr>
                      <td colSpan={7} className="py-16 text-center text-slate-400">
                        <FileText size={40} className="mx-auto mb-3 opacity-30" />
                        <p>No documents found</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Total: {data.pagination.total} files</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="xs" disabled={!data.pagination.hasPrev} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="text-xs text-slate-500 flex items-center px-3">{page} / {data.pagination.totalPages}</span>
                  <Button variant="ghost" size="xs" disabled={!data.pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
